import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Check, BadgeCheck, Layers, Clock, ArrowRight, ShieldQuestion, Sparkles, Share2, Award, Users2, PlayCircle } from "lucide-react";
import api from "@/lib/api";
import { LineReveal, FadeIn, Reveal } from "@/components/Reveal";
import CtaButton from "@/components/CtaButton";
import EnquiryDialog from "@/components/EnquiryDialog";
import Faq from "@/components/Faq";
import EventsStrip from "@/components/EventsStrip";
import Curriculum from "@/components/Curriculum";
import Capstone from "@/components/Capstone";
import ProjectsShowcase from "@/components/ProjectsShowcase";
import BenefitsGrid from "@/components/BenefitsGrid";
import StickyOfferBar, { useStickyAfter } from "@/components/StickyOfferBar";
import PaymentPlans from "@/components/PaymentPlans";
import PaymentDialog from "@/components/PaymentDialog";

// Colored frame around the hero image so a dark course photo sits inside a
// bright border instead of dominating the page as a black box.
const CATEGORY_TINT = {
  Technical: "from-sky to-skylight",
  Business: "from-lemon to-lemonlight",
  Creative: "from-rose to-roselight",
  "Life Skills": "from-teal to-tealight",
};

// One course, one clear CTA. Add a variant here to spin up a new ad page — the
// rest of the page fills itself from the course's own content.
const VARIANTS = {
  default: {
    kicker: "Limited in-person batch",
    headline: () => ["Learn it from someone who's actually done it."],
    promise: (c) => c.summary,
  },
  referral: {
    kicker: "You were referred here",
    headline: () => ["Referred by someone who took this."],
    promise: (c) => `A friend thought this was for you — and they were right. ${c.summary}`,
  },
  career: {
    kicker: "For career-changers",
    headline: () => ["Change track. Learn from a real practitioner."],
    promise: (c) => `Serious about a new direction? ${c.summary}`,
  },
};

export default function Landing() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const vKey = params.get("v") && VARIANTS[params.get("v")] ? params.get("v") : "default";
  const variant = VARIANTS[vKey];
  // Pricing display has 3 variants, switched with ?pricing=…, independent of
  // the ad-copy variant above: "full" (default) shows demo + real payable
  // plans; "demo" shows only the demo, everything else falls back to
  // Enquire; "plans" shows only the payable plans, demo hidden entirely.
  const pricingKey = ["full", "demo", "plans"].includes(params.get("pricing")) ? params.get("pricing") : "full";
  const isPlansOnly = pricingKey === "plans";
  const isDemoOnly = pricingKey === "demo";
  const [course, setCourse] = useState(null);
  const [workshop, setWorkshop] = useState(null);
  const [open, setOpen] = useState(false);
  const [enquiryContext, setEnquiryContext] = useState("");
  const [presetInterest, setPresetInterest] = useState("");
  const [paymentsConfigured, setPaymentsConfigured] = useState(false);
  const [demoPlan, setDemoPlan] = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const showSticky = useStickyAfter(560);

  useEffect(() => {
    api.get(`/courses/${slug}`).then(({ data }) => setCourse(data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!course) return;
    api.get("/workshops").then(({ data }) => {
      setWorkshop(data.workshops.find((w) => w.linked_course === course.slug) || null);
    }).catch(() => {});
  }, [course]);

  // Single canonical check of whether Razorpay is actually configured, and
  // whether this course's demo can be paid online — everything that renders
  // a "Pay" vs "Enquire" button reads from this instead of each doing its
  // own /payments/plans fetch.
  useEffect(() => {
    if (!course?.slug) return;
    api.get(`/payments/plans/${course.slug}`).then(({ data }) => {
      setPaymentsConfigured(!!data.configured);
      setDemoPlan(data.demo_plan || null);
    }).catch(() => {});
  }, [course?.slug]);

  if (!course)
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <p className="font-sans text-xs font-bold text-graphite uppercase tracking-wide">Loading…</p>
      </div>
    );

  const ins = course.instructor;
  const demo = (course.events || []).find((ev) => ev.kind === "demo");
  const isPromptEng = course.slug === "prompt-engineering-fundamentals";
  const showDemo = !isPlansOnly && isPromptEng;
  const effectiveDemo = isPlansOnly ? null : demo;
  const showPricing = !isDemoOnly && !!course.payment_plans;
  const events = isPlansOnly ? (course.events || []).filter((ev) => ev.kind !== "demo") : course.events;
  // The ₹15k→₹10k limited-time discount is a fact about the course, not
  // about which pricing variant is showing — so it's driven off
  // course.payment_plans directly and shown across all 3 ?pricing= variants,
  // even "demo" (which otherwise hides the full plan cards and falls back
  // to Enquire — the discount number still shows there as an incentive).
  const fullUpfrontOption = course.payment_plans?.options?.find((o) => o.key === "full_upfront");
  const hasDiscount = !!(course.payment_plans?.full_price_display && fullUpfrontOption);

  const demoPayable = paymentsConfigured && !!demoPlan;

  const openPaymentPlan = (plan) => {
    if (!paymentsConfigured) {
      openForEnquire();
      return;
    }
    setActivePlan(plan);
    setPaymentDialogOpen(true);
  };
  // When the demo is payable online, "Book a demo" opens the same
  // enquiry-then-Razorpay flow as the full course plans, instead of the
  // plain enquiry dialog — the demo fee is paid the same way everything
  // else is, not a separate special case.
  const openForDemo = () => {
    if (demoPayable) {
      openPaymentPlan(demoPlan);
      return;
    }
    setEnquiryContext("demo"); setPresetInterest(""); setOpen(true);
  };
  const openForEnquire = () => { setEnquiryContext(""); setPresetInterest(""); setOpen(true); };
  const openForProject = (interest) => { setEnquiryContext(""); setPresetInterest(interest || ""); setOpen(true); };
  const scrollToPricing = () => {
    document.getElementById("pricing-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="landing-page" className="bg-paper">
      {/* Minimal header — just the logo, no nav, per brief Section 5 */}
      <header className="w-full mx-auto px-4 md:px-8 lg:px-12 py-5 flex items-center border-b border-line">
        <Link to="/" className="flex items-center gap-2">
          <img src="/fluenex-symbol.svg" alt="" aria-hidden="true" className="h-8 md:h-9 w-auto object-contain" />
          <span className="font-display font-bold text-base md:text-lg tracking-tight text-ink">Fluenex <span className="font-normal">Academy</span></span>
        </Link>
      </header>

      {/* HERO — split layout: text left, large image right, pale tinted background */}
      <section className="mesh-wash relative overflow-hidden">
        <div className="blob-decoration w-[460px] h-[460px] bg-lemonlight -top-40 -right-32" />
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <FadeIn>
                <span className="inline-block rounded-full bg-emeraldlight text-emerald px-4 py-1.5 font-sans text-xs font-bold mb-5">{variant.kicker}</span>
              </FadeIn>
              <h1 className="font-display font-bold tracking-tight text-4xl md:text-5xl leading-[1.08] text-ink">
                <LineReveal lines={variant.headline()} />
              </h1>
              <FadeIn delay={0.4}>
                <p className="font-sans text-xl text-terracotta mt-5 max-w-lg leading-snug font-medium">{variant.promise(course)}</p>
              </FadeIn>
              <FadeIn delay={0.55}>
                <p className="font-sans text-graphite mt-4 max-w-lg text-lg leading-relaxed">{course.intro[0]}</p>
              </FadeIn>
              <FadeIn delay={0.7}>
                <div className="mt-8 flex flex-wrap items-center gap-6">
                  {!course.payment_plans && (
                    <CtaButton onEnquire={openForEnquire} testId="landing-enquire" />
                  )}
                  {showDemo ? (
                    <motion.button
                      type="button"
                      onClick={openForDemo}
                      whileHover={{ y: -2 }}
                      className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-terracotta via-amber-500 to-lemon text-ink px-6 py-3 font-sans text-[15px] font-bold shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <BadgeCheck size={16} />
                      Book a demo · {demo?.fee ?? "₹99"}
                      <span className="opacity-80 inline-block group-hover:translate-x-0.5 transition-transform">→</span>
                    </motion.button>
                  ) : (
                    <div>
                      <p className="font-sans text-xs font-bold text-graphite uppercase tracking-wide">Starting fee</p>
                      <p className="font-display font-bold text-2xl text-emerald">{course.starting_fee} <span className="font-sans text-xs font-semibold text-graphite">{course.fee_note}</span></p>
                    </div>
                  )}
                  {showPricing && (
                    <button
                      type="button"
                      onClick={scrollToPricing}
                      data-testid="landing-view-plans"
                      className="inline-flex items-center gap-1.5 rounded-full border-2 border-emerald text-emerald px-5 py-3 font-sans text-[15px] font-bold hover:bg-emerald/5 transition-colors"
                    >
                      View payment plans
                      <span className="inline-block">↓</span>
                    </button>
                  )}
                </div>
                <p className="font-sans text-xs font-semibold mt-4">
                  {showPricing ? (
                    <span className="text-graphite">
                      <span className="text-terracotta font-bold">Limited time:</span> pay online in full for{" "}
                      {fullUpfrontOption.amount_display}{" "}
                      <span className="line-through text-graphite/50">{course.payment_plans.full_price_display}</span>{" "}
                      — or split into 2 instalments. See options below.
                    </span>
                  ) : hasDiscount ? (
                    <span className="text-graphite">
                      <span className="text-terracotta font-bold">Limited time:</span> {course.payment_plans.full_price_display} →{" "}
                      {fullUpfrontOption.amount_display} if you pay in full — enquire to lock this in.
                    </span>
                  ) : (
                    <span className="text-graphite">
                      No full payment now · we confirm {isPromptEng ? "project plan, " : ""}fee, dates & terms with you directly
                    </span>
                  )}
                </p>
              </FadeIn>
            </div>

            <div className="lg:col-span-6">
              <div className={`relative aspect-[4/3] rounded-[2rem] p-2.5 shadow-lg bg-gradient-to-br ${CATEGORY_TINT[course.category] || "from-violet to-violetlight"}`}>
                <div className="relative h-full w-full rounded-3xl overflow-hidden">
                  <img src={course.image} alt={course.title} className="h-full w-full object-cover" />
                  <span className="absolute top-4 left-4 inline-block rounded-full bg-surface text-emerald font-sans text-xs font-bold px-4 py-2 shadow-sm">
                    {course.category}
                  </span>
                  <div className="absolute bottom-4 left-4 right-4 bg-surface/95 backdrop-blur rounded-2xl p-4 shadow-md flex items-center gap-3">
                    <span className="shrink-0 w-10 h-10 rounded-full bg-emerald text-white flex items-center justify-center">
                      <ShieldCheck size={18} />
                    </span>
                    <p className="font-sans text-sm font-semibold text-ink leading-snug">Verified practitioner · not a career trainer</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* facts strip */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StripFact icon={<Award size={16} />} label="Format" value={course.format} />
            {course.curriculum ? (
              <StripFact icon={<Layers size={16} />} label="Duration" value={formatDuration(course.curriculum)} />
            ) : (
              <StripFact icon={<Layers size={16} />} label="Modules" value={String(course.modules.length)} />
            )}
            <StripFact icon={<BadgeCheck size={16} />} label="Instructor" value="Verified practitioner" />
            {showDemo ? (
              <button
                type="button"
                onClick={openForDemo}
                data-testid="landing-facts-book-demo"
                className="group relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-terracotta via-amber-500 to-lemon text-ink shadow-md hover:shadow-xl transition-all duration-300 text-left"
              >
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/30 blur-2xl pointer-events-none" aria-hidden="true" />
                <div className="relative flex items-center gap-2 text-ink/80">
                  <BadgeCheck size={16} />
                  <span className="font-sans text-xs font-bold uppercase tracking-wide">Demo session</span>
                </div>
                <p className="relative font-display font-black mt-2 text-ink leading-snug text-[17px] md:text-lg">
                  {demo?.fee ?? "₹99"} · Try it live
                  <span className="inline-block ml-1 group-hover:translate-x-1 transition-transform">→</span>
                </p>
                {demo?.duration && (
                  <span className="relative inline-flex items-center gap-1 mt-1.5 rounded-full bg-ink text-white px-2.5 py-1 font-sans text-[10px] font-black uppercase tracking-wide">
                    <Clock size={10} /> {demo.duration}
                  </span>
                )}
                <p className="relative font-sans text-[11px] md:text-xs mt-1.5 text-ink/75 font-semibold">
                  Build a tiny working app
                </p>
              </button>
            ) : (
              <StripFact icon={<Clock size={16} />} label="Fee" value={`${course.starting_fee} ${course.fee_note}`} />
            )}
          </div>

          {/* Upcoming — dated batches and demo sessions */}
          {events && events.length > 0 && (
            <FadeIn delay={0.15}>
              <div className="mt-8">
                <p className="font-sans text-xs font-bold text-terracotta mb-3 uppercase tracking-wide">Upcoming</p>
                <EventsStrip events={events} onEnquire={() => setOpen(true)} testId="landing-events" />
                <p className="font-sans text-xs font-semibold text-graphite mt-3">
                  {showPricing
                    ? "We confirm demo details directly. Full batch fee & instalment options are paid online — see below."
                    : "No payment on this page. We confirm dates and any demo payment with you directly."}
                </p>
              </div>
            </FadeIn>
          )}
        </div>
      </section>

      {/* GAP — big number moment breaks the monotony, per brief Section 5 */}
      {course.gap && (
        <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24 relative overflow-hidden">
          <span className="font-display font-bold text-[9rem] md:text-[12rem] text-emeraldlight leading-none select-none absolute top-8 md:top-10 right-0 md:right-8 pointer-events-none" aria-hidden="true">?</span>
          <Reveal>
            <div className="relative max-w-2xl">
              <p className="font-sans text-xs font-bold text-terracotta mb-3 uppercase tracking-wide">The gap this course closes</p>
              <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl leading-[1.05] text-ink">{course.gap.title}</h2>
              <p className="text-graphite mt-6 leading-relaxed text-lg">{course.gap.body}</p>
            </div>
          </Reveal>
        </section>
      )}

      {/* BENEFITS — the "why this program" grid, strongest marketing hook,
          only for courses that carry a real benefits list. Calm tint band,
          softly animated ambient blobs, equal-height cards with identity. */}
      {course.benefits && course.benefits.length > 0 && (
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-violetlight/50 via-surface/20 to-skylight/30" aria-hidden="true" />
          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute top-0 -left-10 w-[420px] h-[420px] rounded-full bg-emerald/20 blur-3xl"
            animate={{ x: [0, 20, 0], y: [0, -25, 0] }}
            transition={{ duration: 14, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
          />
          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 -right-10 w-[480px] h-[480px] rounded-full bg-violet/20 blur-3xl"
            animate={{ x: [0, -30, 0], y: [0, 15, 0] }}
            transition={{ duration: 18, repeat: Infinity, repeatType: "mirror", ease: "easeInOut", delay: 1 }}
          />
          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute top-1/3 left-1/2 w-[360px] h-[360px] rounded-full bg-terracotta/10 blur-3xl"
            animate={{ x: [0, 20, -10, 0], y: [0, -10, 18, 0] }}
            transition={{ duration: 22, repeat: Infinity, repeatType: "mirror", ease: "easeInOut", delay: 2 }}
          />
          <div className="w-full mx-auto px-4 md:px-8 lg:px-12 relative">
            <Reveal>
              <div className="max-w-3xl">
                <motion.span
                  className="inline-flex items-center gap-2 rounded-full bg-emerald text-white px-4 py-1.5 font-sans text-xs font-bold mb-5 shadow-sm"
                  whileHover={{ scale: 1.03 }}
                >
                  <ShieldCheck size={12} /> Why this program
                </motion.span>
                <motion.h2
                  className="font-display font-bold tracking-tight text-3xl md:text-5xl leading-[1.05] text-ink"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  Built to actually be worth your <span className="text-terracotta">time</span>.
                </motion.h2>
                <motion.p
                  className="text-graphite mt-5 text-lg leading-relaxed max-w-2xl"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                  Honest, practical training that gets you building real work — not watching someone else do it. Every benefit below ships with the program, no strings attached.
                </motion.p>
              </div>
            </Reveal>
            <div className="mt-12">
              <BenefitsGrid benefits={course.benefits} testId="landing-benefits" />
            </div>
          </div>
        </section>
      )}

      {/* OUTCOMES — on stone band for rhythm */}
      <section className="bg-stone py-16 md:py-24">
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12">
          <Reveal>
            <p className="font-sans text-xs font-bold text-terracotta mb-3 uppercase tracking-wide">What you'll walk away with</p>
            <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl max-w-3xl leading-[1.05] text-ink">Real skill you can use — not theory you'll forget.</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 items-stretch">
            {course.what_you_learn.map((w, i) => (
              <Reveal key={i} delay={(i % 2) * 0.06} className="h-full">
                <div className="flex items-start gap-4 rounded-2xl bg-surface p-5 shadow-sm h-full">
                  <span className="shrink-0 w-9 h-9 rounded-full bg-emeraldlight text-emerald flex items-center justify-center"><Check size={16} /></span>
                  <p className="text-lg leading-snug">{w}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSTRUCTOR — full-width feature, largest single element on the page */}
      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <Reveal>
          <div className="rounded-3xl overflow-hidden shadow-md border border-line">
            <div className="gradient-brand h-2" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              <div className="lg:col-span-5 relative aspect-[4/3] lg:aspect-auto">
                <img src={course.image} alt={ins.name} className="absolute inset-0 h-full w-full object-cover" />
              </div>
              <div className="lg:col-span-7 bg-surface p-8 md:p-12">
              <div className="flex items-center gap-3 text-emerald mb-4">
                <BadgeCheck size={20} />
                <span className="font-sans text-xs font-bold uppercase tracking-wide">Why this teacher — verified before onboarding</span>
              </div>
              <h3 className="font-display font-bold tracking-tight text-3xl md:text-4xl text-ink">{ins.name}</h3>
              <p className="font-sans text-xs font-bold text-terracotta mt-3 uppercase tracking-wide">{ins.role}</p>
              <p className="text-graphite mt-5 leading-relaxed text-lg">{ins.bio}</p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {ins.track_record.map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] text-ink/85"><Check size={16} className="mt-1 shrink-0 text-emerald" /><span>{t}</span></li>
                ))}
              </ul>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CURRICULUM — week-grouped with deliverables when the course has one,
          otherwise the generic flat module grid */}
      <section className="bg-stone topo-texture py-16 md:py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-emerald via-terracotta to-violet" aria-hidden="true" />
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12 relative">
          <Reveal>
            <div className="max-w-4xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-terracotta/15 border border-terracotta/30 text-terracotta px-4 py-1.5 font-sans text-xs font-bold mb-4">
                <Clock size={12} /> 6 weeks · small batch · in-person
              </span>
              <p className="font-sans text-xs font-bold text-terracotta mb-3 uppercase tracking-wide">What's inside</p>
              <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl max-w-3xl leading-[1.05] text-ink">
                {course.curriculum ? "Your week-by-week learning journey." : "How the batch is structured."}
              </h2>
              <p className="text-graphite mt-5 text-lg leading-relaxed max-w-2xl">
                {course.curriculum
                  ? "Every week has a clear focus, live sessions, and something you actually build or ship. Click any week to expand it — deliverables are marked so you always know what you walk away with."
                  : "Modules are grouped around live build sessions so you learn by doing, not by watching."}
              </p>
            </div>
          </Reveal>
          <div className="mt-12">
            {course.curriculum ? (
              <Curriculum weeks={course.curriculum} testId="landing-curriculum" />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {course.modules.map((m, i) => (
                  <Reveal key={i} delay={(i % 2) * 0.06}>
                    <div className="rounded-2xl bg-surface p-6 h-full shadow-sm">
                      <span className="font-display font-bold text-4xl text-terracotta block leading-none">{String(i + 1).padStart(2, "0")}</span>
                      <h4 className="font-display font-semibold text-xl mt-4">{m.title}</h4>
                      <p className="text-graphite mt-2 leading-relaxed text-[15px]">{m.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PROJECTS — portfolio-style showcase, only for courses that carry real projects */}
      {course.projects && course.projects.length > 0 && (
        <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24 relative overflow-hidden">
          <div className="absolute top-24 left-0 w-80 h-80 bg-sky/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="absolute bottom-1/3 right-0 w-80 h-80 bg-rose/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
          <Reveal>
            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-terracotta/15 to-amber-500/15 border border-terracotta/30 text-terracotta px-4 py-1.5 font-sans text-xs font-bold mb-4">
                <Sparkles size={12} /> Pick a path — or bring your own idea
              </span>
              <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl max-w-4xl leading-[1.05] text-ink">
                What you could build.
              </h2>
              <p className="text-graphite mt-5 max-w-2xl text-lg leading-relaxed">
                These are real examples of projects previous students have built during the program using prompt engineering.
                They're options, not a fixed list — pick one, mix ideas, or come with your own completely different project. We help you choose when you enquire.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 relative">
            <ProjectsShowcase projects={course.projects} testId="landing-projects" onEnquire={openForProject} />
          </div>
        </section>
      )}

      {/* CAPSTONE — final project journey, only for courses that have one */}
      {course.capstone && (
        <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <Reveal>
            <p className="font-sans text-xs font-bold text-terracotta mb-3 uppercase tracking-wide">Final capstone</p>
            <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl max-w-3xl leading-[1.05] text-ink">{course.capstone.title}</h2>
          </Reveal>
          <div className="mt-10">
            <Capstone capstone={course.capstone} testId="landing-capstone" />
          </div>
        </section>
      )}

      {/* WHO IT'S FOR */}
      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-20">
        <Reveal>
          <p className="font-sans text-xs font-bold text-terracotta mb-3 uppercase tracking-wide">Who it's for</p>
          <div className="flex flex-wrap gap-3">
            {course.who_its_for.map((w, i) => (
              <span key={i} className="rounded-full bg-emeraldlight text-emerald px-5 py-2.5 text-[15px] font-bold">{w}</span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* GUARANTEE + AFTER PROGRAMME — equal-height pair, same content volume */}
      {(course.guarantee || course.after_programme) && (
        <section className="w-full mx-auto px-4 md:px-8 lg:px-12 pb-16 md:pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {course.guarantee && (
              <Reveal>
                <div className="rounded-3xl bg-emeraldlight p-8 h-full flex flex-col">
                  <span className="shrink-0 w-14 h-14 rounded-2xl bg-emerald text-white flex items-center justify-center mb-5">
                    <ShieldCheck size={26} />
                  </span>
                  <p className="font-sans text-xs font-bold text-terracotta mb-2 uppercase tracking-wide">The guarantee</p>
                  <p className="font-serif italic text-2xl leading-snug text-ink/80">{course.guarantee}</p>
                </div>
              </Reveal>
            )}
            {course.after_programme && (
              <Reveal delay={0.06}>
                <div className="rounded-3xl bg-surface border border-line p-8 h-full flex flex-col shadow-sm">
                  <span className="shrink-0 w-14 h-14 rounded-2xl bg-terracotta/10 text-terracotta flex items-center justify-center mb-5">
                    <Award size={26} />
                  </span>
                  <p className="font-sans text-xs font-bold text-terracotta mb-2 uppercase tracking-wide">After the programme</p>
                  <p className="text-lg leading-relaxed text-ink/90">{course.after_programme}</p>
                </div>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* COMPARISON — full width */}
      {course.comparison && (
        <section className="w-full mx-auto px-4 md:px-8 lg:px-12 pb-16 md:pb-24">
          <Reveal>
            <div className="rounded-3xl border border-line bg-surface p-8 md:p-10 shadow-sm">
              <p className="font-sans text-xs font-bold text-terracotta mb-3 uppercase tracking-wide">How this compares</p>
              <p className="text-lg leading-relaxed text-graphite max-w-3xl">{course.comparison}</p>
            </div>
          </Reveal>
        </section>
      )}

      {/* FREE WORKSHOP */}
      {workshop && (
        <section className="w-full mx-auto px-4 md:px-8 lg:px-12 pb-16 md:pb-24">
          <Reveal>
            <Link
              to={`/workshops/${workshop.slug}`}
              data-testid="landing-workshop-cta"
              className="group rounded-3xl border-2 border-dashed border-terracotta/40 hover:border-terracotta bg-surface p-8 flex flex-col md:flex-row md:items-center gap-6 transition-colors"
            >
              <div className="flex-1">
                <p className="font-sans text-xs font-bold text-terracotta mb-2 uppercase tracking-wide">Not ready to enquire yet?</p>
                <h3 className="font-display font-semibold text-2xl leading-tight">{workshop.title}</h3>
                <p className="text-graphite mt-2 text-[15px] max-w-xl leading-relaxed">{workshop.summary}</p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-2 font-sans font-bold text-terracotta group-hover:text-terracottahover">
                See the free workshop <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </Reveal>
        </section>
      )}

      {/* FAQ */}
      {course.faq && course.faq.length > 0 && (
        <section className="bg-stone py-16 md:py-24">
          <div className="w-full mx-auto px-4 md:px-8 lg:px-12">
            <Reveal>
              <p className="font-sans text-xs font-bold text-terracotta mb-3 flex items-center gap-2 uppercase tracking-wide">
                <ShieldQuestion size={14} /> Questions people actually ask
              </p>
              <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl leading-[1.05] mb-8 text-ink max-w-2xl">Before you enquire.</h2>
              <div className="max-w-3xl">
                <Faq items={course.faq} testId="landing-faq" />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* WHAT'S INCLUDED + PRICE */}
      {course.included && (
        <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <Reveal>
            <div className="rounded-3xl bg-surface border-2 border-emerald p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7">
                  <p className="font-sans text-xs font-bold text-emerald mb-4 flex items-center gap-2 uppercase tracking-wide">
                    <Sparkles size={14} /> What's included
                  </p>
                  <ul className="space-y-3">
                    {course.included.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-[15px] md:text-base text-ink/85">
                        <Check size={16} className="mt-1 shrink-0 text-emerald" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-5 lg:border-l lg:border-line lg:pl-10">
                  {hasDiscount ? (
                    <>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-terracotta text-white px-3 py-1.5 font-sans text-[11px] font-black uppercase tracking-wide mb-4">
                        <Clock size={12} /> Limited time discount
                      </span>
                      <p className="font-sans text-xs font-bold text-graphite uppercase tracking-wide">Course fee</p>
                      <div className="flex items-end gap-3 mt-2 flex-wrap">
                        <span className="font-sans text-2xl text-graphite/50 line-through">
                          {course.payment_plans.full_price_display}
                        </span>
                        <span className="font-display font-bold tracking-tight text-5xl text-emerald">
                          {fullUpfrontOption.amount_display}
                        </span>
                      </div>
                      <p className="font-sans text-sm font-bold text-emerald mt-1.5">
                        if you pay the whole fee at once — you save ₹5,000
                      </p>
                      <p className="text-graphite text-[15px] mt-4 leading-relaxed">
                        {showPricing
                          ? <>Prefer to split it? Pay{" "}
                              {course.payment_plans.options.find((o) => o.key === "installment_1")?.amount_display} now and
                              the same again before the batch starts.</>
                          : "This price won't last — book the demo to hold your seat before it ends."}
                      </p>
                      <div className="mt-6">
                        {showPricing ? (
                          <button
                            type="button"
                            onClick={scrollToPricing}
                            data-testid="landing-view-plans-included"
                            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald text-white px-5 py-3.5 font-sans font-bold hover:bg-emeraldhover transition-colors"
                          >
                            See payment options ↓
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={openForDemo}
                            data-testid="landing-demo-included"
                            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-terracotta text-white px-5 py-3.5 font-sans font-bold hover:bg-terracottahover transition-colors"
                          >
                            Book a demo · {demo?.fee ?? "₹99"}
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="font-sans text-xs font-bold text-graphite uppercase tracking-wide">Starting fee</p>
                      <div className="flex items-end gap-2 mt-2">
                        <span className="font-display font-bold tracking-tight text-5xl text-emerald">{course.starting_fee}</span>
                        <span className="font-sans text-xs font-semibold text-graphite mb-2">{course.fee_note}</span>
                      </div>
                      <p className="text-graphite text-[15px] mt-4 leading-relaxed">
                        A starting figure. Exact amount, batch dates and any instalment options are confirmed with you directly.
                      </p>
                      <div className="mt-6">
                        <CtaButton onEnquire={openForEnquire} full testId="landing-enquire-included" />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* PRICING & PAYMENT — demo + payable plans, only for courses with real payment_plans */}
      {showPricing && (
        <section id="pricing-section" className="w-full mx-auto px-4 md:px-8 lg:px-12 pb-16 md:pb-24 scroll-mt-24">
          <Reveal>
            <p className="font-sans text-xs font-bold text-terracotta mb-3 uppercase tracking-wide">Pricing</p>
            <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl max-w-3xl leading-[1.05] text-ink">
              {effectiveDemo
                ? "Not sure yet? Start with the demo. Ready? Pick how you'd like to pay."
                : "Pick how you'd like to pay."}
            </h2>
          </Reveal>
          <div className="mt-10">
            <PaymentPlans
              course={course}
              demo={effectiveDemo}
              configured={paymentsConfigured}
              onEnquire={openForEnquire}
              onDemoEnquire={openForDemo}
              onSelectPlan={openPaymentPlan}
            />
          </div>
        </section>
      )}

      {/* FINAL CTA — pale, color reserved for accents */}
      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 pb-16 md:pb-24">
        <div className="bg-stone rounded-3xl px-8 md:px-16 py-14 md:py-20 relative overflow-hidden">
          <div className="blob-decoration w-96 h-96 bg-tealight -bottom-32 -right-20" />
          <div className="relative max-w-3xl">
            <h2 className="font-display font-bold tracking-tight text-3xl md:text-6xl leading-[1.0] text-ink">
              Ready to learn <span className="text-emerald">{course.title.split("&")[0].trim()}</span> from someone who's done it?
            </h2>
            <p className="text-graphite mt-5 text-lg leading-relaxed">
              Seats in each batch are limited so the room stays small.{" "}
              {showPricing
                ? "Pick a plan above to pay online."
                : hasDiscount
                ? "Book the demo below to hold your seat — limited-time pricing on the full course."
                : "Enquire now and we'll take it from there — no payment on this page."}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              {showPricing ? (
                <button
                  type="button"
                  onClick={scrollToPricing}
                  data-testid="landing-view-plans-bottom"
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald text-white px-6 py-3.5 font-sans text-[15px] font-bold shadow-md hover:bg-emeraldhover transition-colors"
                >
                  View payment plans ↑
                </button>
              ) : hasDiscount ? (
                <button
                  type="button"
                  onClick={openForDemo}
                  data-testid="landing-demo-bottom"
                  className="inline-flex items-center gap-1.5 rounded-full bg-terracotta text-white px-6 py-3.5 font-sans text-[15px] font-bold shadow-md hover:bg-terracottahover transition-colors"
                >
                  Book a demo · {demo?.fee ?? "₹99"}
                </button>
              ) : (
                <CtaButton onEnquire={() => setOpen(true)} testId="landing-enquire-bottom" />
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="w-full mx-auto px-4 md:px-8 lg:px-12 py-8 border-t border-line flex items-center justify-between pb-24 md:pb-8">
        <p className="font-sans text-[11px] font-semibold text-graphite">Fluenex Academy · {course.category}</p>
        <p className="font-sans text-[11px] font-semibold text-graphite">Every fee is a starting figure</p>
      </footer>

      <StickyOfferBar
        show={showSticky}
        fact={course.starting_fee}
        factLabel="Starting fee"
        demo={effectiveDemo}
        onEnquire={openForEnquire}
        onDemoEnquire={openForDemo}
        onViewPlans={showPricing ? scrollToPricing : undefined}
        hideEnquire={!!course.payment_plans}
        primaryMode={showDemo ? "demo" : "enquire"}
        testId="landing-sticky-bar"
      />

      <EnquiryDialog
        open={open}
        onClose={() => { setOpen(false); setPresetInterest(""); }}
        course={course}
        source={enquiryContext === "demo" ? `landing:${vKey}:demo` : `landing:${vKey}`}
        presetInterest={presetInterest}
      />

      <PaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        course={course}
        plan={activePlan}
      />
    </div>
  );
}

// A course's curriculum may or may not end in a bonus week (e.g. the trading
// program has one, this doesn't) — count regular weeks and call out the
// bonus week only if the data actually has one, instead of assuming it does.
function formatDuration(weeks) {
  const bonus = weeks.filter((w) => /bonus/i.test(w.week));
  const core = weeks.length - bonus.length;
  return bonus.length > 0 ? `${core} weeks + ${bonus.length} bonus` : `${weeks.length} weeks`;
}

function StripFact({ icon, label, value }) {
  return (
    <div className="rounded-2xl bg-surface p-4 shadow-sm">
      <div className="flex items-center gap-2 text-terracotta">{icon}<span className="font-sans text-xs font-bold uppercase tracking-wide">{label}</span></div>
      <p className="text-[15px] mt-2 leading-snug text-ink/85 line-clamp-1">{value}</p>
    </div>
  );
}
